import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { listAssessments, assignAssessmentExpert, listExperts } from '../services/adminApi';
import { formatDate } from '../utils/formatters';
import StatusChip from '../components/common/StatusChip';
import ScrollableChipRow from '../components/common/ScrollableChipRow';

const FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Pending Review', value: 'pending_review' },
  { label: 'Completed', value: 'completed' },
];

export default function AssessmentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [experts, setExperts] = useState([]);
  const [selectedExpertId, setSelectedExpertId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAssessments = useCallback(() => {
    setLoading(true);
    const filters = filter !== 'all' ? { status: filter } : {};
    listAssessments(filters)
      .then((data) => setAssessments(data.items || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  const handleRowClick = (params) => {
    setSelectedAssessment(params.row);
    setDrawerOpen(true);
  };

  const handleAssignOpen = () => {
    listExperts()
      .then((data) => setExperts(data.items || []))
      .catch(() => setExperts([]));
    setAssignOpen(true);
  };

  const handleAssignExpert = async () => {
    if (!selectedAssessment || !selectedExpertId) return;
    setActionLoading(true);
    try {
      await assignAssessmentExpert(
        selectedAssessment.id || selectedAssessment.assessment_id,
        selectedExpertId
      );
      setAssignOpen(false);
      setSelectedExpertId('');
      setDrawerOpen(false);
      fetchAssessments();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      field: 'assessment_id',
      headerName: 'Assessment ID',
      width: 150,
      valueGetter: (params) => params.row.assessment_id || params.row.id || '-',
    },
    { field: 'client_name', headerName: 'Client', flex: 1, minWidth: 140, valueGetter: (params) => params.row.client_name || params.row.client_email || '-' },
    { field: 'intern_name', headerName: 'Intern', flex: 1, minWidth: 140, valueGetter: (params) => params.row.intern_name || params.row.intern_email || '-' },
    { field: 'expert_name', headerName: 'Mentor', flex: 1, minWidth: 140, valueGetter: (params) => params.row.expert_name || params.row.expert_email || 'Unassigned' },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      renderCell: (params) => <StatusChip status={params.value} type="assessment" />,
    },
    {
      field: 'scheduled_time',
      headerName: 'Scheduled',
      width: 170,
      valueFormatter: (params) => formatDate(params.value),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Assessments
      </Typography>

      <ScrollableChipRow>
        {FILTER_OPTIONS.map((opt) => (
          <Chip
            key={opt.value}
            label={opt.label}
            onClick={() => setFilter(opt.value)}
            color={filter === opt.value ? 'primary' : 'default'}
            variant={filter === opt.value ? 'filled' : 'outlined'}
          />
        ))}
      </ScrollableChipRow>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && assessments.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No assessments in this view</Typography>
            </Paper>
          )}
          {!loading && assessments.map((a) => (
            <Paper
              key={a.id || a.assessment_id || Math.random()}
              sx={{ p: 2, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
              onClick={() => handleRowClick({ row: a })}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'monospace' }} noWrap>
                    {(a.assessment_id || a.id || '').slice(0, 8)}…
                  </Typography>
                  <Typography variant="body2" noWrap>
                    {a.client_name || a.client_email || '—'}
                  </Typography>
                </Box>
                <StatusChip status={a.status} type="assessment" />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Intern: {a.intern_name || a.intern_email || '—'} · Mentor: {a.expert_name || a.expert_email || 'Unassigned'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Scheduled: {formatDate(a.scheduled_time)}
              </Typography>
            </Paper>
          ))}
        </Stack>
      ) : (
      <Box sx={{ height: 600, backgroundColor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={assessments}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id || row.assessment_id || Math.random()}
          onRowClick={handleRowClick}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableRowSelectionOnClick
        />
      </Box>
      )}

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 420 } } }}
      >
        {selectedAssessment && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Assessment Details</Typography>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <DetailRow label="Assessment ID" value={selectedAssessment.assessment_id || selectedAssessment.id} />
              <DetailRow label="Client" value={selectedAssessment.client_name || selectedAssessment.client_email} />
              <DetailRow label="Intern" value={selectedAssessment.intern_name || selectedAssessment.intern_email} />
              <DetailRow label="Mentor" value={selectedAssessment.expert_name || selectedAssessment.expert_email || 'Unassigned'} />
              <DetailRow label="Service Type" value={selectedAssessment.service_type} />
              <DetailRow label="Scheduled Time" value={formatDate(selectedAssessment.scheduled_time)} />
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip status={selectedAssessment.status} type="assessment" />
                </Box>
              </Box>
              <DetailRow label="Report URL" value={selectedAssessment.report_url} />
              <DetailRow label="Notes" value={selectedAssessment.notes} />
              <DetailRow label="Created" value={formatDate(selectedAssessment.created_at)} />
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Button
              variant="contained"
              startIcon={<VerifiedUserIcon />}
              onClick={handleAssignOpen}
              fullWidth
            >
              Assign Mentor
            </Button>
          </Box>
        )}
      </Drawer>

      {/* Assign Mentor Dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Mentor to Assessment</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Select Mentor</InputLabel>
            <Select
              value={selectedExpertId}
              onChange={(e) => setSelectedExpertId(e.target.value)}
              label="Select Mentor"
            >
              {experts.map((expert) => (
                <MenuItem key={expert.id || expert.email} value={expert.id || expert.email}>
                  {expert.name || expert.email} {expert.specialization ? `(${expert.specialization})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleAssignExpert} variant="contained" disabled={!selectedExpertId || actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function DetailRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
